import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from 'react-query';

import {
  type BillingPeriod,
  type ProductAllowance,
  type PreviewCostsResponse,
  SubscriptionCreationOrderService,
  useSubscriptionInfo,
  countriesWithRequiredStates,
  isAllBasic,
} from '@hotjar/subscription-management';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import type { OwnedAccount } from '@hotjar/account';
import type { CountryCodesForRequiredStates } from '@hotjar/country-lib';
import type { Currency } from '@hotjar/product-catalog';

interface UseOrderPreviewProps {
  account: Pick<OwnedAccount, 'id' | 'features'> | undefined;
  billingPeriod: BillingPeriod | undefined;
  productAllowances: Array<ProductAllowance> | undefined;
}

/**
 * Wraps a create subscription order preview call with react-query.
 * The hook can be used to preview what the account's subscription would cost, given an array of
 * selected allowances.
 */
export const useCreateSubscriptionOrderPreview = ({
  account,
  billingPeriod = 'MONTHLY',
  productAllowances = [],
}: UseOrderPreviewProps): UseQueryResult<PreviewCostsResponse | undefined> => {
  const { data: subscriptionInfo } = useSubscriptionInfo({ enabled: isBillingV3Enabled(account) });
  const { customer, user } = subscriptionInfo || {};

  const country = customer?.billingAddress?.country ?? user?.country ?? 'US';
  const countryDefaults = countriesWithRequiredStates[country as CountryCodesForRequiredStates];

  const subscriptionCreationOrderService = useMemo(() => {
    if (!account) {
      return null;
    }

    return new SubscriptionCreationOrderService(account.id);
  }, [account]);

  return useQuery<PreviewCostsResponse | undefined>({
    queryKey: [
      'trials',
      'create-subscription-order-preview',
      account?.id,
      billingPeriod,
      productAllowances,
    ],
    queryFn: async (): Promise<PreviewCostsResponse | undefined> => {
      if (!isBillingV3Enabled(account) || isAllBasic(productAllowances)) {
        return undefined;
      }

      return subscriptionCreationOrderService?.preview({
        billingPeriod,
        customer: {
          companyName: customer?.companyName ?? user?.companyName ?? '',
          additionalEmailAddresses: customer?.additionalEmailAddresses ?? [],
          currency: customer?.currency ?? (user?.currency.toUpperCase() as Currency) ?? 'EUR',
          billingAddress: customer?.billingAddress ?? {
            zipCode: countryDefaults?.zipCode ?? null,
            country,
            emailAddress: user?.emailAddress ?? '',
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
            state: countryDefaults?.state ?? null,
          },
          currentPeriodCostSummary: customer?.currentPeriodCostSummary,
          taxNumber: customer?.taxNumber,
        },
        allowancesToAdd: productAllowances,
        allowancesToRemove: [],
      });
    },
    cacheTime: 60 * 60 * 1000, // 1 hour
    staleTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    retry: false,
  });
};
